import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Upload, X, ImageOff, Search, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DropZone from "@/components/DropZone";
import { uploadImage } from "@/lib/imageUpload";

const PAGE_SIZE = 60;

/**
 * Modal for the RichTextEditor "Insert image" button. Two ways to add an image:
 *  1. Pick one from the library of previously-uploaded images (grid, newest first)
 *  2. Upload a new image directly — hits `POST /api/images/upload` and inserts it
 *
 * Performance notes:
 *  - Library is paginated (60 images per page). Big libraries used to spam the
 *    browser with 400+ 4-5MB PNGs; now we only load what fits in the viewport.
 *  - Debounced server-side search — the `q` parameter is passed to the API so
 *    we never over-fetch on typing.
 *  - `loading="lazy"` still helps for the tiles below the fold within a page.
 *  - "Clean up library" button (admin only) purges orphan + duplicate DB rows
 *    so long-running libraries stay small and fast.
 *
 * Calls `onPick(url)` with the `/api/files/{path}` URL when the admin selects one.
 */
export default function ImagePickerModal({ open, onClose, onPick }) {
  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [cleanupPreview, setCleanupPreview] = useState(null); // {orphans, duplicates, kept, total}
  const [cleaning, setCleaning] = useState(false);
  const fileRef = useRef(null);
  // Debounce timer so we don't hammer the API on every keystroke.
  const searchTimer = useRef(null);
  // Race-condition guards: cancel the previous in-flight request and only apply
  // the response if it matches the latest issued request id. Prevents a slow
  // "c" response from clobbering a fast "cat" response when the user is typing.
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchPage = async (skip, searchTerm, append = false) => {
    // Abort any in-flight fetch so stale responses can't overwrite fresh state.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* no-op */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const reqId = ++requestIdRef.current;

    const setLoad = append ? setLoadingMore : setLoading;
    setLoad(true);
    try {
      const r = await api.get("/images", {
        params: { skip, limit: PAGE_SIZE, q: searchTerm || undefined },
        signal: controller.signal,
      });
      // Defensive: bail if a newer request has since started.
      if (reqId !== requestIdRef.current) return;
      const items = r.data?.items || [];
      setImages(prev => append ? [...prev, ...items] : items);
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
    } catch (e) {
      // Silently swallow aborted requests — the newer fetch owns the UI.
      if (e?.name === "CanceledError" || e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
      if (reqId !== requestIdRef.current) return;
      if (!append) setImages([]);
    } finally {
      if (reqId === requestIdRef.current) setLoad(false);
    }
  };

  // Abort any in-flight request on unmount so we don't leak setState calls.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* no-op */ }
      }
    };
  }, []);

  // Initial load + reset when modal opens
  useEffect(() => {
    if (!open) {
      // Cancel any in-flight request so a late response can't repopulate a
      // closed modal or bleed into the next open.
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* no-op */ }
      }
      setQ(""); setImages([]); setCleanupPreview(null);
      return;
    }
    fetchPage(0, "", false);
  }, [open]);

  // Debounced re-fetch when search term changes (skip initial mount which the
  // `open` effect already covers).
  useEffect(() => {
    if (!open) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchPage(0, q, false), 250);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadImage(file);
      setImages(prev => [data, ...prev]);
      setTotal(t => t + 1);
      toast.success("Uploaded — inserting into blog");
      onPick(data.url);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openCleanupDialog = async () => {
    try {
      const { data } = await api.get("/images/cleanup/preview");
      const totalRemove = (data.orphans?.count || 0) + (data.duplicates?.count || 0);
      if (totalRemove === 0) {
        toast.success("Library is already clean — nothing to remove.");
        return;
      }
      setCleanupPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup preview failed");
    }
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/images/cleanup");
      const mb = ((data.freed_bytes || 0) / (1024 * 1024)).toFixed(1);
      toast.success(`Removed ${data.removed} unused images — ${mb} MB freed`);
      setCleanupPreview(null);
      // Refresh the library
      fetchPage(0, q, false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const removeSummary = useMemo(() => {
    if (!cleanupPreview) return null;
    const c = (cleanupPreview.orphans?.count || 0) + (cleanupPreview.duplicates?.count || 0);
    const b = (cleanupPreview.orphans?.bytes || 0) + (cleanupPreview.duplicates?.bytes || 0);
    return { count: c, bytes: b };
  }, [cleanupPreview]);

  if (!open) return null;

  return createPortal(
    <div data-testid="image-picker-modal"
         className="fixed inset-0 z-[9999] bg-black/50 grid place-items-center p-4"
         style={{ pointerEvents: "auto" }}
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col shadow-xl">
        <DropZone
          multiple={false}
          onFiles={(files) => handleUpload(files[0])}
          className="flex flex-col min-h-0 flex-1"
          label="Drop image to upload"
          testId="image-picker-dropzone"
        >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <div className="font-heading font-semibold text-slate-900 dark:text-slate-100">Image library</div>
            <div className="text-xs text-slate-500">
              <span data-testid="image-picker-total">{total}</span> image{total === 1 ? "" : "s"} · showing {images.length}
            </div>
          </div>
          <button onClick={onClose} data-testid="image-picker-close"
                  className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
            <X className="w-4 h-4"/>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"/>
            <input value={q} onChange={e => setQ(e.target.value)}
                   data-testid="image-picker-search"
                   placeholder="Search filename or alt text…"
                   className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded"/>
          </div>
          <button
            type="button"
            data-testid="image-picker-cleanup"
            onClick={openCleanupDialog}
            title="Remove unused / duplicate images from the library"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-slate-300 hover:bg-slate-50 text-slate-600"
          >
            <Trash2 className="w-3.5 h-3.5"/>Clean up
          </button>
          <input id="image-picker-file" ref={fileRef} type="file" accept="image/*"
                 data-testid="image-picker-file-input"
                 onChange={e => handleUpload(e.target.files?.[0])}
                 className="sr-only"/>
          <label htmlFor="image-picker-file"
                 data-testid="image-picker-upload"
                 aria-disabled={uploading}
                 className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded text-white select-none cursor-pointer
                             ${uploading ? "bg-sky-400 pointer-events-none" : "bg-sky-700 hover:bg-sky-600"}`}>
            <Upload className="w-3.5 h-3.5"/> {uploading ? "Uploading…" : "Upload new"}
          </label>
        </div>

        {/* Grid */}
        <div className="p-4 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm italic">
              <Loader2 className="w-4 h-4 animate-spin"/> Loading library…
            </div>
          )}
          {!loading && images.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 gap-2">
              <ImageOff className="w-8 h-8"/>
              <div className="text-sm italic">
                {q.trim() ? `No images match "${q}"` : "No images yet — upload one to get started."}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map(img => (
              <button key={img.image_id}
                      data-testid={`image-picker-item-${img.image_id}`}
                      onClick={() => { onPick(img.url); onClose(); }}
                      className="group relative border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden hover:border-sky-500 hover:shadow-md transition-all text-left bg-slate-50 dark:bg-slate-800">
                <div className="aspect-square bg-slate-100 dark:bg-slate-800 grid place-items-center overflow-hidden">
                  <img src={img.url} alt={img.alt || img.filename || ""}
                       loading="lazy"
                       decoding="async"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                </div>
                <div className="px-2 py-1.5 text-[10px] text-slate-600 dark:text-slate-400 truncate">
                  {img.filename || img.path?.split("/").pop()}
                </div>
              </button>
            ))}
          </div>
          {hasMore && !loading && (
            <div className="flex justify-center mt-4">
              <button
                type="button"
                data-testid="image-picker-load-more"
                disabled={loadingMore}
                onClick={() => fetchPage(images.length, q, true)}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50 text-slate-700 disabled:opacity-50"
              >
                {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null}
                {loadingMore ? "Loading…" : `Load ${Math.min(PAGE_SIZE, total - images.length)} more`}
              </button>
            </div>
          )}
        </div>
        </DropZone>

        {/* Cleanup confirmation */}
        {cleanupPreview && (
          <div
            data-testid="image-picker-cleanup-dialog"
            className="absolute inset-0 z-10 bg-black/50 grid place-items-center p-4"
            onClick={() => setCleanupPreview(null)}
          >
            <div onClick={e => e.stopPropagation()}
                 className="bg-white dark:bg-slate-900 rounded-lg max-w-md w-full p-5 shadow-xl border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-2 text-amber-700 font-heading font-semibold mb-2">
                <Trash2 className="w-5 h-5"/> Clean up library
              </div>
              <div className="text-sm text-slate-700 dark:text-slate-300 space-y-2">
                <p>
                  This will remove <strong data-testid="image-picker-cleanup-count">{removeSummary?.count ?? 0}</strong> unused
                  {" "}image record{removeSummary?.count === 1 ? "" : "s"}
                  {" "}
                  ({((removeSummary?.bytes || 0) / (1024 * 1024)).toFixed(1)} MB) from the library.
                </p>
                <ul className="text-xs list-disc list-inside text-slate-500 space-y-0.5">
                  <li><strong>{cleanupPreview.orphans?.count || 0}</strong> orphaned uploads (not referenced by any blog, pathway, glossary, or setting)</li>
                  <li><strong>{cleanupPreview.duplicates?.count || 0}</strong> duplicate DB rows (same file, older sibling records)</li>
                  <li><strong>{cleanupPreview.kept}</strong> kept — actively used elsewhere in the site</li>
                </ul>
                <p className="text-[11px] text-slate-500 leading-snug">
                  Underlying object storage is left intact. Only the DB records go away, so a restored blog can still find its images.
                </p>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  data-testid="image-picker-cleanup-cancel"
                  onClick={() => setCleanupPreview(null)}
                  disabled={cleaning}
                  className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  data-testid="image-picker-cleanup-confirm"
                  onClick={runCleanup}
                  disabled={cleaning}
                  className="px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Trash2 className="w-3.5 h-3.5"/>}
                  {cleaning ? "Cleaning…" : `Remove ${removeSummary?.count ?? 0}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
